/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { FC, memo, useCallback, useState } from 'react';
import Loader from '../../../../../shared/components/molecules/loader';
import { LOADING } from '../../../../../shared/constants';
import usePaginatedQuery from '../../../../../shared/hooks/usePaginatedQuery';
import { AgentAvatar } from '../../../../home/v1/atoms/agent-avatar';
import OwnerRow from './owner-row';
import { getOrgOwners } from './requests';
import { orgOwnersAtom } from './state';
import { OrgOwner, OrgOwnersPaginationParams } from './typings';

interface OrgOwnersListProps {
  featureName?: string;
}

const INITIAL_VISIBLE_COUNT = 3;
const NEXT_PREVIEW_COUNT = 3;
const PILL_SIZE_PX = 26;
const PILL_OVERLAP_PX = 8;
const ROW_HEIGHT_PX = 56;

const pillAvatarCss = css`
  border: 2px solid #ffffff;
  box-sizing: border-box;
`;

const OrgOwnersList: FC<OrgOwnersListProps> = ({ featureName }) => {
  const [expanded, setExpanded] = useState(false);

  const ownersGetter = useCallback(async (params?: OrgOwnersPaginationParams) => {
    const data = await getOrgOwners(params);
    const paginationData: OrgOwnersPaginationParams = {
      sortAfter: data?.nextSortAfter,
      sortAfterDate: data?.nextSortAfterDate,
      hasMore: data?.hasMore ?? false,
    };
    return {
      data: data?.owners ?? [],
      paginationData,
    };
  }, []);

  const {
    requestData: { loading, data: owners, error },
  } = usePaginatedQuery<OrgOwner, OrgOwnersPaginationParams>({
    requestAtom: orgOwnersAtom,
    getter: ownersGetter,
    cacheData: true,
  });

  const toggleExpanded = useCallback(() => setExpanded((prev) => !prev), []);

  if (loading && !owners?.length) {
    return (
      <div
        css={css`
          width: 100%;
          max-width: 460px;
          height: ${ROW_HEIGHT_PX}px;
          display: flex;
          align-items: center;
          justify-content: center;
        `}
      >
        <Loader size={20} secondary={LOADING} />
      </div>
    );
  }

  if (error || !owners?.length) {
    return null;
  }

  const visibleOwners = expanded ? owners : owners.slice(0, INITIAL_VISIBLE_COUNT);
  const hiddenCount = Math.max(0, owners.length - INITIAL_VISIBLE_COUNT);
  const showToggle = expanded || hiddenCount > 0;
  const nextOwnersPreview = !expanded
    ? owners.slice(INITIAL_VISIBLE_COUNT, INITIAL_VISIBLE_COUNT + NEXT_PREVIEW_COUNT)
    : [];

  return (
    <div
      css={css`
        width: 100%;
        max-width: 460px;
        border-radius: 12px;
        border: 1px solid #e6e6e6;
        background-color: #ffffff;
        padding: 4px 8px;
        max-height: 70vh;
        overflow-y: auto;
      `}
    >
      {visibleOwners.map((owner) => (
        <OwnerRow key={owner.id} owner={owner} featureName={featureName} />
      ))}
      {showToggle && (
        <div>
          <div
            css={css`
              width: 100%;
              height: 1px;
              background-color: rgba(0, 0, 0, 0.06);
            `}
          />
          <button
            type="button"
            onClick={toggleExpanded}
            css={css`
              width: 100%;
              height: 45px;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              background: none;
              border: none;
              cursor: pointer;
              font-family: inherit;
              padding: 0 10px;
              border-radius: 7px;
              color: #307e5b;
              &:hover {
                background-color: rgba(48, 126, 91, 0.06);
              }
            `}
          >
            {!expanded && nextOwnersPreview.length > 0 && (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                `}
              >
                {nextOwnersPreview.map((owner, i) => (
                  <div
                    key={owner.id}
                    css={css`
                      margin-left: ${i === 0 ? 0 : -PILL_OVERLAP_PX}px;
                    `}
                  >
                    <AgentAvatar name={owner.name} phone={owner.phone} size={PILL_SIZE_PX} avatarCss={pillAvatarCss} />
                  </div>
                ))}
              </div>
            )}
            <span
              css={css`
                font-size: 13px;
                font-weight: 500;
                line-height: 1;
              `}
            >
              {expanded ? 'Show less' : `Show ${hiddenCount} more`}
            </span>
            {expanded ? (
              <KeyboardArrowUpIcon
                css={css`
                  font-size: 18px;
                `}
              />
            ) : (
              <KeyboardArrowDownIcon
                css={css`
                  font-size: 18px;
                `}
              />
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default memo(OrgOwnersList);
